import { useMutation, useQuery } from 'react-query';
import PageableRq from '@domain/rq/common/PageableRq';
import { ContentsListItemVO } from '@domain/vo/common/ContentsListItemVO';
import { PositionBookmarkListItemVO } from '@domain/vo/common/PositionBookmarkListItemVO';
import { RemotePhsCenterRepo } from '@repository/phs/center/PhsCenterRepo';
import { PhsPositionsRq } from '@domain/rq/phs/center/PhsPositionsRq';
import { PhsKnowledgeAndSkillsRq } from '@domain/rq/phs/center/PhsKnowledgeAndSkillsRq';
import { PhsSkillPositionsRq } from '@domain/rq/phs/center/PhsSkillPositionsRq';
import { V2Route } from '@utils/routeUtil';

const repo = new RemotePhsCenterRepo();
// const repo = new PhsCenterMock();

const phsCenterKeys = ['phs', 'center'];

const staleTime = 1000 * 60 * 3;

const usePhsCenterContents = (rq: PageableRq) => {
  return useQuery([...phsCenterKeys, 'contents'], async () => {
    const { data } = await repo.fetchPhsCenterContents(rq);
    return {
      pages: data.pages,
      posts: data.posts?.map(
        (dto) =>
          new ContentsListItemVO({
            ...dto,
            name: dto.categoryName,
            tag: dto.label,
            routeUrl: V2Route.getInfoDetailPage(dto.sn),
          }),
      ),
    };
  });
};

const usePhsCenterPosition = (rq: PhsPositionsRq) => {
  return useQuery(
    [...phsCenterKeys, 'position', rq.jobTitleCode],
    async () => {
      const { data } = await repo.fetchPhsCenterPosition(rq);
      return {
        pages: data.pages,
        positions: data.positions.map(
          (dto) =>
            new PositionBookmarkListItemVO({
              ...dto,
              teamImageUrl: dto.thumbnailImageUrl,
            }),
        ),
      };
    },
    { keepPreviousData: true, staleTime },
  );
};

const usePhsCenterResultType = (enabled?: boolean) => {
  return useQuery(
    [...phsCenterKeys, 'resultType'],
    async () => {
      const { data } = await repo.fetchPhsCenterResultType();
      return data;
    },
    { enabled },
  );
};

const usePhsCenterSkill = (enabled: boolean) => {
  return useQuery(
    [...phsCenterKeys, 'skill'],
    async () => {
      const { data } = await repo.fetchPhsCenterSkill();
      return data;
    },
    { enabled },
  );
};

const usePutPhsCenterSkill = () => {
  return useMutation(async (rq: PhsKnowledgeAndSkillsRq) => {
    const { data } = await repo.fetchPutPhsCenterSkill(rq);
    return data;
  });
};

const usePhsCenterSkillPosition = (rq: PhsSkillPositionsRq, enabled: boolean) => {
  return useQuery(
    [...phsCenterKeys, 'skillPosition', rq.skillCode],
    async () => {
      const { data } = await repo.fetchPhsCenterSkillPosition(rq);
      return {
        positions: data.positions?.map(
          (dto) =>
            new PositionBookmarkListItemVO({
              ...dto,
              teamImageUrl: dto.thumbnailImageUrl,
            }),
        ),
      };
    },
    { enabled, keepPreviousData: true, staleTime },
  );
};

export {
  phsCenterKeys,
  usePhsCenterContents,
  usePhsCenterPosition,
  usePhsCenterResultType,
  usePhsCenterSkill,
  usePutPhsCenterSkill,
  usePhsCenterSkillPosition,
};
